import { decode } from 'cbor2'
import { u8ToHex } from './utils'

type Resolver = (data: any) => void
export type NotificationsListener = (
  messageType: number,
  data: Map<number, any>,
) => void

export interface WebSocketDispatcher {
  send<T>(messageId: Uint8Array, data: Uint8Array): Promise<T>
  setNotificationsListener(listener: NotificationsListener): void
  removeNotificationsListener(): void
  close(): void
}

const MESSAGE_TIMEOUT_MS = 15_000

export function createWebSocketDispatcher(
  socket: WebSocket,
): WebSocketDispatcher {
  const pending = new Map<
    string,
    { resolve: Resolver; reject: (err: Error) => void }
  >()
  let notificationsListener: NotificationsListener | null = null

  const rejectAllPending = (reason: string) => {
    pending.forEach(({ reject }) => reject(new Error(reason)))
    pending.clear()
  }

  const handleMessage = async (event: MessageEvent) => {
    const buffer = await getDataBuffer(event.data)
    if (!buffer) return

    try {
      const decoded = decode<Map<number, any>>(buffer)
      const messageHeader = decoded.get(0) as Map<number, any>
      let messageId = u8ToHex(messageHeader.get(3) as Uint8Array)
      if (pending.has(messageId)) {
        const { resolve } = pending.get(messageId)!
        pending.delete(messageId)
        resolve(decoded)
      } else {
        const messageType = messageHeader.get(2) as number
        notificationsListener?.(messageType, decoded)
      }
    } catch (err) {
      console.error('CBOR decode failed:', err)
    }
  }

  const handleClose = () => {
    rejectAllPending('WebSocket connection closed')
  }

  const handleError = () => {
    rejectAllPending('WebSocket error')
  }

  socket.addEventListener('message', handleMessage)
  socket.addEventListener('close', handleClose)
  socket.addEventListener('error', handleError)

  const send = <T>(messageId: Uint8Array, data: Uint8Array): Promise<T> => {
    console.log(`send message at ${new Date().toISOString()}`)
    return new Promise((resolve, reject) => {
      if (socket.readyState !== WebSocket.OPEN) {
        return reject(new Error('WebSocket is not open'))
      }
      const sendMessageId = u8ToHex(messageId)

      const timer = setTimeout(() => {
        if (pending.has(sendMessageId)) {
          pending.delete(sendMessageId)
          reject(new Error(`Message timed out after ${MESSAGE_TIMEOUT_MS}ms`))
        }
      }, MESSAGE_TIMEOUT_MS)

      pending.set(sendMessageId, {
        resolve: response => {
          clearTimeout(timer)
          resolve(response as T)
        },
        reject: err => {
          clearTimeout(timer)
          reject(err)
        },
      })

      try {
        socket.send(data)
      } catch (err) {
        clearTimeout(timer)
        console.log('error sending message', err)
        pending.delete(sendMessageId)
        reject(err)
      }
    })
  }

  const setNotificationsListener = (listener: NotificationsListener) => {
    notificationsListener = listener
  }

  const removeNotificationsListener = () => {
    notificationsListener = null
  }

  const close = () => {
    socket.removeEventListener('message', handleMessage)
    socket.removeEventListener('close', handleClose)
    socket.removeEventListener('error', handleError)
    rejectAllPending('Dispatcher closed')
  }

  return {
    send,
    setNotificationsListener,
    removeNotificationsListener,
    close,
  }
}

async function getDataBuffer(
  data: Blob | ArrayBuffer,
): Promise<Uint8Array | null> {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data)
  }
  if (data instanceof Blob) {
    const arrayBuffer = await data.arrayBuffer()
    return new Uint8Array(arrayBuffer)
  }
  return null
}
